import styled from '@emotion/styled';
import { DirectorUser } from '@kaizenplatform/hugot-entity';
import { AppBar as _AppBar, Button, Toolbar } from '@material-ui/core';
import {
  AddCircle as _AddCircle,
  Help as _Help,
  Home as _Home,
  LibraryBooks as _LibraryBooks,
  PlayCircleFilled as _PlayCircleFilled,
} from '@material-ui/icons';
import { createElement, FC, MouseEventHandler } from 'react';

import Language from '../../../../entity/Language';
import { OrganizationDetailFragment } from '../../../../generated/graphql';
import { itemBackground } from '../../../constant/color';
import { SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import _KaizenAdLogo from '../../primitive/KaizenLogo';
import SideBarInternalLinkMenuButton from './SideBarInternalLinkMenuButton';
import UserPopupMenuBase from './UserPopupMenu';

export const SIDEBAR_WIDTH = 90;
export const SIDEBAR_HEIGHT = 56;

type Props = {
  availableOrganizations: Array<OrganizationDetailFragment>;
  selectedOrganization: OrganizationDetailFragment | null;
  signedInUser: DirectorUser;
  onChangeSelectedOrganization: (organization: OrganizationDetailFragment) => void;
  onClickHomeButton: MouseEventHandler<HTMLElement>;
  onClickProjectsButton: MouseEventHandler<HTMLElement>;
  onClickNewProjectButton: MouseEventHandler<HTMLElement>;
  onClickCreative: MouseEventHandler<HTMLElement>;
  onClickAnalyticsButton: MouseEventHandler<HTMLElement>;
  onClickLogo: MouseEventHandler<HTMLElement>;
  onClickSettings: MouseEventHandler<HTMLElement>;
  onClickAccountSettings: MouseEventHandler<HTMLElement>;
  onClickSignOut: MouseEventHandler<HTMLElement>;
  language: Language;
  className?: string;
};

const SideBar: FC<Props> = ({
  availableOrganizations,
  selectedOrganization,
  signedInUser,
  onChangeSelectedOrganization,
  onClickHomeButton,
  onClickProjectsButton,
  onClickNewProjectButton,
  onClickCreative,
  // onClickAnalyticsButton,
  onClickLogo,
  onClickSettings,
  onClickAccountSettings,
  onClickSignOut,
  className,
  language,
}) => {
  return (
    <AppBar color="default" elevation={2} className={className} data-testid="SideBar">
      <StyledToolbar>
        <LogoContainer onClick={onClickLogo}>
          <KaizenAdLogo />
        </LogoContainer>

        <HomeButtonContainer
          onClick={onClickHomeButton}
          path={'/latest-insights'}
          messageId={'sidebar.home'}
          dataIntercomTarget={'sidebar-home-button'}
          icon={<Home />}
        />

        <NewProjectButtonContainer
          onClick={onClickNewProjectButton}
          path={'/project_types'}
          messageId={'sidebar.new_project'}
          dataIntercomTarget={'sidebar-new-project-button'}
          icon={<AddCircle />}
        />

        <ProjectListButtonContainer
          onClick={onClickProjectsButton}
          path={'/projects'}
          messageId={'sidebar.project_list'}
          dataIntercomTarget={'sidebar-project-list-button'}
          icon={<LibraryBooks />}
        />

        {(selectedOrganization?.businessRegion.key === 'jp' || (!selectedOrganization && language === 'ja-JP')) && (
          <CreativeButtonContainer
            onClick={onClickCreative}
            path={'/search'}
            messageId={'sidebar.creative'}
            dataIntercomTarget={'sidebar-creative-search-button'}
            icon={<PlayCircleFilled />}
          />
        )}

        {/* <CreativeAnalyticsButtonContainer */}
        {/*   onClick={onClickAnalyticsButton} */}
        {/*   path={'/ad-creatives'} */}
        {/*   messageId={'sidebar.analytics'} */}
        {/*   dataIntercomTarget={'sidebar-creative-analytics-button'} */}
        {/*   icon={<BarChart />} */}
        {/* /> */}

        <UserPopupMenu
          signedInUser={signedInUser}
          onClickSettings={onClickSettings}
          onClickSignOut={onClickSignOut}
          onClickAccountSettings={onClickAccountSettings}
          availableOrganizations={availableOrganizations}
          selectedOrganization={selectedOrganization}
          onChangeSelectedOrganization={onChangeSelectedOrganization}
        />

        <HelpButtonContainer href="https://help.kaizen-ad.com/" target="_blank" rel="noopener noreferrer">
          <HELP />
        </HelpButtonContainer>
      </StyledToolbar>
    </AppBar>
  );
};

const AppBar = styled(_AppBar)({
  width: SIDEBAR_WIDTH,
  height: '100%',
  paddingLeft: SPACING_UNIT / 2,
  paddingRight: '0 !important',
  right: 'auto',
  left: '0',
  backgroundColor: '#fff',
  '::before': {
    position: 'absolute',
    top: '-1px',
    left: '-1px',
    display: 'block',
    width: 6,
    height: '100vh',
    background: `linear-gradient(#49de9e, #42c5cf)`,
    content: '""',
    zIndex: 10,
  },
});

const StyledToolbar = styled(Toolbar)(
  gridLayoutContainer({
    columns: ['1fr'],
    rows: ['80px', '80px', '80px', '80px', '80px', '80px', 'auto', '50px', '50px'],
    areas: [
      ['logo'],
      ['latest_insight_list'],
      ['new_project'],
      ['project_list'],
      ['creative'],
      ['creative-analytics'],
      ['blank'],
      ['user_menu'],
      ['help'],
    ],
    alignItems: 'center',
  }),
  {
    padding: '0',
    height: '100%',
    width: '100%',
  },
);

const LogoContainer = styled('div')(gridLayoutItem('logo'), {
  textAlign: 'center',
  cursor: 'pointer',
  width: '100%',
});

const KaizenAdLogo = styled(_KaizenAdLogo)({
  paddingBottom: 2,
  width: 32,
});

const SideBarButtonIconBase = {
  width: 32,
  height: 32,
  color: '#4C566C',
};

const HomeButtonContainer = styled(SideBarInternalLinkMenuButton)(gridLayoutItem('latest_insight_list'), {});
const Home = styled(_Home)(SideBarButtonIconBase, {});
const NewProjectButtonContainer = styled(SideBarInternalLinkMenuButton)(gridLayoutItem('new_project'), {});
const AddCircle = styled(_AddCircle)(SideBarButtonIconBase, {});
const ProjectListButtonContainer = styled(SideBarInternalLinkMenuButton)(gridLayoutItem('project_list'), {});
const LibraryBooks = styled(_LibraryBooks)(SideBarButtonIconBase, {});
const CreativeButtonContainer = styled(SideBarInternalLinkMenuButton)(gridLayoutItem('creative'), {});
const PlayCircleFilled = styled(_PlayCircleFilled)(SideBarButtonIconBase, {});
// const CreativeAnalyticsButtonContainer = styled(SideBarInternalLinkMenuButton)(gridLayoutItem('creative-analytics'),
// {}); const BarChart = styled(_BarChart)(SideBarButtonIconBase, {});

const HelpButtonContainer = styled('a')(gridLayoutItem('help'), {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  cursor: 'pointer',
  width: '100%',
  padding: SPACING_UNIT,
  '&:hover': {
    background: itemBackground,
    transition: 'background 0.3s ease',
  },
});
const HELP = styled(_Help)(SideBarButtonIconBase, {});

export const AnalyticsButton = styled(Button)(gridLayoutItem('analytics_buttons'), {
  [mq('sp')]: {
    display: 'none',
  },
});

export const UserPopupMenu = styled(UserPopupMenuBase)(gridLayoutItem('user_menu'), {
  '& aside': {
    [mq('sp')]: {
      display: 'none',
    },
  },
});

export default SideBar;
